import { motion } from "framer-motion";
import { ABOUT } from "@/constants/testIds";
import { ShieldCheck, Rocket, Users2, Boxes } from "lucide-react";

const stats = [
  { key: "years", value: "8+", label: "Years shipping" },
  { key: "projects", value: "60+", label: "Launched projects" },
  { key: "clients", value: "40+", label: "Global partners" },
  { key: "uptime", value: "99.9%", label: "Systems uptime" },
];

const pillars = [
  { icon: ShieldCheck, title: "Enterprise-grade", desc: "Trusted by government and Fortune-league partners across APAC." },
  { icon: Rocket, title: "Ship velocity", desc: "Weekly builds. No monthly waterfall. Real production every sprint." },
  { icon: Users2, title: "Studio-craft", desc: "Small pods, senior operators. No junior-first churn on your product." },
  { icon: Boxes, title: "Full stack", desc: "Web, mobile, CMS, e-commerce — one team owns end to end." },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid={ABOUT.section}
      className="relative py-24 md:py-32 border-t border-cyan-900/40 overflow-hidden"
    >
      <div className="absolute inset-0 hud-grid opacity-25 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline">/ 03 studio.dossier</div>
            <h2 className="mt-4 font-mono-display text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tighter text-slate-100">
              About <span className="text-neon-cyan">UM Software</span>
            </h2>
            <p className="mt-6 text-slate-300 font-body text-base leading-relaxed">
              UM Software is a Jakarta-based software development studio delivering innovative technology
              solutions across industries. We specialize in custom applications, mobile software, and
              enterprise-level systems — engineered to enhance operational efficiency and accelerate
              digital transformation.
            </p>
            <p className="mt-4 text-slate-400 font-body leading-relaxed">
              Our commitment to quality is non-negotiable. We employ cutting-edge tech to deliver scalable,
              sustainable solutions that let organizations hit their objectives without babysitting the stack.
            </p>

            <div className="mt-8 p-5 border border-cyan-500/25 bg-[#0a0a0a]">
              <div className="font-hud text-cyan-400/80 mb-2">// mission.log</div>
              <p className="font-mono-display text-lg text-slate-100 leading-snug">
                &ldquo;To deliver the best possible solution for every partner — through digital products that outlive trends.&rdquo;
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-5">
            {stats.map((s, i) => (
              <motion.div
                key={s.key}
                data-testid={ABOUT.stat(s.key)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-6 md:p-7 bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/40 transition-colors duration-300 hud-corners"
              >
                <span className="hud-c-tl" />
                <span className="hud-c-br" />
                <div className="font-hud text-cyan-400/70">/ metric</div>
                <div className="mt-3 font-mono-display text-4xl md:text-5xl text-neon-cyan">{s.value}</div>
                <div className="mt-1 text-slate-400 font-body text-sm">{s.label}</div>
              </motion.div>
            ))}

            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }}
                  className="col-span-2 sm:col-span-1 p-5 md:p-6 bg-transparent border border-white/5 hover:border-fuchsia-500/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
                >
                  <span className="w-9 h-9 flex items-center justify-center border border-fuchsia-500/40 text-fuchsia-400">
                    <Icon size={16} />
                  </span>
                  <h4 className="mt-4 font-mono-display uppercase tracking-tight text-slate-100">{p.title}</h4>
                  <p className="mt-2 text-slate-400 text-sm font-body">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
